import React from 'react';
import { cn } from '@/lib/core/utils';
import { Calendar, Clock, AlertTriangle, CheckCircle } from 'lucide-react';
import type { CurrentShift } from '@/hooks/kiosk';

interface ShiftMatchDisplayProps {
  shift: CurrentShift | undefined;
  className?: string;
}

/**
 * Displays matched shift information with early/late status
 */
export function ShiftMatchDisplay({ shift, className }: ShiftMatchDisplayProps) {
  if (!shift) {
    return (
      <div className={cn(
        'p-4 rounded-xl bg-gray-50 dark:bg-muted border border-gray-200 dark:border-border',
        className
      )}>
        <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
          <Calendar className="w-5 h-5" />
          <span>No scheduled shift for today</span>
        </div>
      </div>
    );
  }

  const formatTime = (isoString: string) => {
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getStatusDisplay = () => {
    if (shift.is_late) {
      return {
        icon: AlertTriangle,
        text: `${shift.minutes_late} minutes late`,
        color: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-50 dark:bg-red-500/10',
        borderColor: 'border-red-200 dark:border-red-500/30',
      };
    }
    if (shift.is_early && shift.minutes_early && shift.minutes_early > 30) {
      return {
        icon: Clock,
        text: `${shift.minutes_early} minutes early`,
        color: 'text-orange-600 dark:text-orange-400',
        bgColor: 'bg-orange-50 dark:bg-orange-500/10',
        borderColor: 'border-orange-200 dark:border-orange-500/30',
      };
    }
    return {
      icon: CheckCircle,
      text: 'On time',
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-500/10',
      borderColor: 'border-green-200 dark:border-green-500/30',
    };
  };

  const status = getStatusDisplay();
  const StatusIcon = status.icon;

  return (
    <div className={cn(
      'p-4 rounded-xl',
      status.bgColor,
      'border',
      status.borderColor,
      className
    )}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          <div>
            <p className="text-gray-500 dark:text-gray-400 text-sm">Scheduled shift</p>
            <p className="text-gray-900 dark:text-gray-100 font-medium">
              {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
            </p>
          </div>
        </div>

        <div className={cn('flex items-center gap-2', status.color)}>
          <StatusIcon className="w-5 h-5" />
          <span className="font-medium">{status.text}</span>
        </div>
      </div>
    </div>
  );
}
